import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { callMonitorAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  PhoneIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  SparklesIcon,
  PlayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Status → badge styles
const STATUS_BADGE = {
  queued:        'bg-gray-100 text-gray-700',
  ringing:       'bg-blue-100 text-blue-700',
  'in-progress': 'bg-indigo-100 text-indigo-700',
  forwarding:    'bg-violet-100 text-violet-700',
  ended:         'bg-green-100 text-green-700',
  completed:     'bg-green-100 text-green-700',
  failed:        'bg-red-100 text-red-700',
  'no-answer':   'bg-amber-100 text-amber-700',
  busy:          'bg-amber-100 text-amber-700',
  cancelled:     'bg-gray-100 text-gray-500',
};

// Score → color
const scoreCls = (score) => {
  if (score == null) return 'bg-gray-100 text-gray-500';
  if (score >= 80)   return 'bg-green-100 text-green-700';
  if (score >= 60)   return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

function Spinner({ size = 'sm' }) {
  const s = size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-6 h-6' : 'w-8 h-8';
  return <div className={`${s} border-2 border-current border-t-transparent rounded-full animate-spin`} />;
}

const fmtDuration = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = 'gray', icon }) {
  const accents = {
    gray:   'border-gray-200 text-gray-800',
    blue:   'border-blue-200 text-blue-800',
    green:  'border-green-200 text-green-800',
    amber:  'border-amber-200 text-amber-800',
    red:    'border-red-200 text-red-800',
  };
  return (
    <div className={`bg-white border rounded-xl p-4 ${accents[accent] || accents.gray}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-70">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Call detail modal ────────────────────────────────────────────────────────

function CallDetailModal({ call, onClose, onReanalyse, onRefresh, onDelete, isAdmin }) {
  if (!call) return null;
  let analysis = null;
  try { analysis = call.analysis_json ? JSON.parse(call.analysis_json) : null; } catch {}

  const categoryScores = analysis?.category_scores || {};
  const findings       = analysis?.findings || [];
  const compliance     = analysis?.compliance || {};

  const findingCfg = {
    info:     { cls: 'bg-blue-50 border-blue-200 text-blue-800',     icon: <ExclamationCircleIcon className="w-4 h-4 text-blue-500" /> },
    warning:  { cls: 'bg-amber-50 border-amber-200 text-amber-800',  icon: <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" /> },
    critical: { cls: 'bg-red-50 border-red-200 text-red-800',        icon: <ExclamationCircleIcon className="w-4 h-4 text-red-500" /> },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900">Call to {call.target_phone_number || '—'}</h2>
              <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_BADGE[call.status] || 'bg-gray-100 text-gray-700'}`}>
                {call.status}
              </span>
              {call.quality_score != null && (
                <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${scoreCls(call.quality_score)}`}>
                  Score: {call.quality_score}/100
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {fmtDateTime(call.started_at)} · {fmtDuration(call.duration_sec)} · triggered by {call.triggered_by}
              {call.ended_reason && <> · ended: {call.ended_reason}</>}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {call.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <strong>Error:</strong> {call.error_message}
            </div>
          )}

          {analysis && (
            <>
              {/* Verdict & summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4 text-primary-600" />
                  <h3 className="text-sm font-semibold text-gray-800">AI Verdict: {analysis.verdict || '—'}</h3>
                </div>
                {analysis.summary && (
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{analysis.summary}</p>
                )}
              </div>

              {/* Category scores */}
              {Object.keys(categoryScores).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Category Scores</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(categoryScores).map(([k, v]) => (
                      <div key={k} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="text-xs uppercase text-gray-500 font-semibold tracking-wide">{k}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="text-xl font-bold text-gray-900">{v}</div>
                          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full ${
                                v >= 80 ? 'bg-green-500' : v >= 60 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths / weaknesses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(analysis.strengths || []).length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Strengths</h4>
                    <ul className="space-y-1 text-sm text-green-900">
                      {analysis.strengths.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                )}
                {(analysis.weaknesses || []).length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">Weaknesses</h4>
                    <ul className="space-y-1 text-sm text-red-900">
                      {analysis.weaknesses.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {/* Findings */}
              {findings.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Findings</h3>
                  <div className="space-y-2">
                    {findings.map((f, i) => {
                      const cfg = findingCfg[f.severity] || findingCfg.info;
                      return (
                        <div key={i} className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${cfg.cls}`}>
                          {cfg.icon}
                          <span>{f.message}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Compliance checks */}
              {Object.keys(compliance).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Compliance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(compliance).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        {v
                          ? <CheckCircleIcon className="w-4 h-4 text-green-500" />
                          : <ExclamationCircleIcon className="w-4 h-4 text-red-500" />}
                        <span className="text-gray-700 capitalize">{k.replaceAll('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {(analysis.recommendations || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Recommendations</h3>
                  <ul className="space-y-2">
                    {analysis.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!analysis && call.has_transcript && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4" />
              No analysis yet. Click "Analyse" to generate one.
            </div>
          )}

          {/* Recording */}
          {call.recording_url && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Recording</h3>
              <audio controls src={call.recording_url} className="w-full" />
              <a
                href={call.recording_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary-600 hover:underline mt-1 inline-block"
              >
                Open in new tab
              </a>
            </div>
          )}

          {/* Transcript */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Transcript</h3>
            {call.transcript ? (
              <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">
                {call.transcript}
              </pre>
            ) : (
              <div className="text-sm text-gray-400 italic">No transcript yet — try the Refresh button if the call is still in progress.</div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={onDelete}
                className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5"
              >
                <TrashIcon className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {call.vapi_call_id && (
              <button
                onClick={onRefresh}
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
              </button>
            )}
            {call.has_transcript && isAdmin && (
              <button
                onClick={onReanalyse}
                className="px-3 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 inline-flex items-center gap-1.5"
              >
                <SparklesIcon className="w-3.5 h-3.5" /> {analysis ? 'Re-analyse' : 'Analyse'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function CallMonitor() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(true);
  const [summary, setSummary] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [activeCall, setActiveCall] = useState(null);

  // ── Loaders ──
  // Settings are read-only here (managed in Admin → Integrations). We still load
  // them to gate the Trigger button and show the "Next Scheduled" stat. Non-admins
  // can't read them, so fall back to a minimal object.
  const loadSettings = useCallback(async () => {
    if (!isAdmin) {
      setSettings({
        enabled: false,
        target_phone_number: null,
        target_name: 'Customer Hotline',
        vapi_assistant_id: null,
        schedule_day_of_week: 'mon',
        schedule_hour: 9,
        schedule_minute: 0,
        vapi_configured: false,
        twilio_configured: false,
      });
      return;
    }
    try {
      const res = await callMonitorAPI.getSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Settings load failed', err);
    }
  }, [isAdmin]);

  const loadCalls = useCallback(async () => {
    try {
      const res = await callMonitorAPI.listCalls({ limit: 50 });
      setCalls(res.data);
    } catch (err) {
      console.error('Calls load failed', err);
    } finally {
      setLoadingCalls(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await callMonitorAPI.weeklySummary();
      setSummary(res.data);
    } catch (err) {
      console.error('Summary load failed', err);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadCalls();
    loadSummary();
  }, [loadSettings, loadCalls, loadSummary]);

  // Auto-poll while any call is in-flight (since the backend poller runs every minute)
  useEffect(() => {
    const inFlight = calls.some(c => !['ended', 'failed', 'no-answer', 'busy', 'completed', 'cancelled'].includes(c.status));
    if (!inFlight) return;
    const t = setInterval(loadCalls, 8000);
    return () => clearInterval(t);
  }, [calls, loadCalls]);

  // ── Handlers ──
  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await callMonitorAPI.trigger();
      toast.success('Call initiated — Vapi is dialling…');
      setCalls(prev => [res.data, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger call');
    } finally {
      setTriggering(false);
    }
  };

  const openCall = async (call) => {
    try {
      const res = await callMonitorAPI.getCall(call.id);
      setActiveCall(res.data);
    } catch {
      toast.error('Failed to load call details');
    }
  };

  const handleRefreshActiveCall = async () => {
    if (!activeCall) return;
    try {
      const res = await callMonitorAPI.refreshCall(activeCall.id);
      setActiveCall(res.data);
      loadCalls();
      toast.success('Refreshed from Vapi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
    }
  };

  const handleReanalyseActiveCall = async () => {
    if (!activeCall) return;
    try {
      await callMonitorAPI.reanalyse(activeCall.id);
      toast.success('Analysis started — refresh in a moment');
      setTimeout(async () => {
        try {
          const res = await callMonitorAPI.getCall(activeCall.id);
          setActiveCall(res.data);
          loadCalls();
        } catch {}
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-analyse failed');
    }
  };

  const handleDeleteActiveCall = async () => {
    if (!activeCall) return;
    if (!window.confirm('Delete this call log? This cannot be undone.')) return;
    try {
      await callMonitorAPI.deleteCall(activeCall.id);
      setCalls(prev => prev.filter(c => c.id !== activeCall.id));
      setActiveCall(null);
      toast.success('Call deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // ── Derived stats ──
  const completedScores = calls.filter(c => c.quality_score != null).map(c => c.quality_score);
  const avgScore = completedScores.length
    ? Math.round(completedScores.reduce((a, b) => a + b, 0) / completedScores.length)
    : null;
  const lastCall = calls[0];
  const nextScheduled = settings?.enabled
    ? `${(settings.schedule_day_of_week || 'mon').toUpperCase()} ${String(settings.schedule_hour ?? 9).padStart(2, '0')}:${String(settings.schedule_minute ?? 0).padStart(2, '0')}`
    : 'Disabled';

  const canTrigger = isAdmin
    && settings?.vapi_configured
    && settings?.twilio_configured
    && settings?.vapi_assistant_id
    && settings?.target_phone_number;

  return (
    <div className="p-6 space-y-6 overflow-y-auto h-full bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <PhoneArrowUpRightIcon className="w-6 h-6 text-indigo-600" />
            Call Quality Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Automated mystery-customer calls to your hotline, with AI quality scoring</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleTrigger}
            disabled={!canTrigger || triggering}
            title={!canTrigger ? 'Configure Vapi + Twilio + hotline number in Admin → Integrations first' : 'Place a test call right now'}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-40"
          >
            {triggering ? <Spinner /> : <PlayIcon className="w-4 h-4" />}
            Trigger Call Now
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Total Calls"
          value={calls.length}
          sub={`${calls.filter(c => c.status === 'ended' || c.status === 'completed').length} completed`}
          accent="blue"
          icon={<PhoneIcon className="w-3.5 h-3.5" />}
        />
        <StatCard
          label="Avg Quality Score"
          value={avgScore != null ? `${avgScore}/100` : '—'}
          sub={completedScores.length ? `${completedScores.length} scored` : 'No scored calls yet'}
          accent={avgScore == null ? 'gray' : avgScore >= 80 ? 'green' : avgScore >= 60 ? 'amber' : 'red'}
          icon={<ChartBarIcon className="w-3.5 h-3.5" />}
        />
        <StatCard
          label="Last Call"
          value={lastCall ? fmtDateTime(lastCall.started_at).split(',')[0] : '—'}
          sub={lastCall ? `Status: ${lastCall.status}` : '—'}
          accent="gray"
          icon={<ClockIcon className="w-3.5 h-3.5" />}
        />
        <StatCard
          label="Next Scheduled"
          value={nextScheduled}
          sub={settings?.target_phone_number || 'Set a hotline number'}
          accent={settings?.enabled ? 'green' : 'gray'}
          icon={<PhoneArrowUpRightIcon className="w-3.5 h-3.5" />}
        />
      </div>

      {/* Weekly summary */}
      {summary && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-primary-600" />
              This Week's Summary
            </h2>
            <span className="text-xs text-gray-400">
              {new Date(summary.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              {' – '}
              {new Date(summary.week_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="text-center bg-gray-50 rounded-lg py-2">
              <div className="text-xs uppercase text-gray-500 font-semibold tracking-wide">Total</div>
              <div className="text-xl font-bold text-gray-900">{summary.total_calls}</div>
            </div>
            <div className="text-center bg-green-50 rounded-lg py-2">
              <div className="text-xs uppercase text-green-600 font-semibold tracking-wide">Completed</div>
              <div className="text-xl font-bold text-green-700">{summary.completed_calls}</div>
            </div>
            <div className="text-center bg-red-50 rounded-lg py-2">
              <div className="text-xs uppercase text-red-600 font-semibold tracking-wide">Failed</div>
              <div className="text-xl font-bold text-red-700">{summary.failed_calls}</div>
            </div>
            <div className="text-center bg-indigo-50 rounded-lg py-2">
              <div className="text-xs uppercase text-indigo-600 font-semibold tracking-wide">Avg Score</div>
              <div className="text-xl font-bold text-indigo-700">
                {summary.avg_quality_score != null ? Math.round(summary.avg_quality_score) : '—'}
              </div>
            </div>
          </div>

          {(summary.common_strengths.length > 0 || summary.common_weaknesses.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              {summary.common_strengths.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-green-700 uppercase mb-1">Common Strengths</div>
                  <ul className="text-sm text-green-900 space-y-0.5">
                    {summary.common_strengths.map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                </div>
              )}
              {summary.common_weaknesses.length > 0 && (
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-red-700 uppercase mb-1">Common Weaknesses</div>
                  <ul className="text-sm text-red-900 space-y-0.5">
                    {summary.common_weaknesses.map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {summary.recommendation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              <strong className="text-blue-700">Recommendation:</strong> {summary.recommendation}
            </div>
          )}
        </div>
      )}

      {/* Call history */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Call History</h2>
          <button
            onClick={loadCalls}
            className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {loadingCalls ? (
          <div className="flex justify-center py-10">
            <Spinner size="md" />
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <PhoneXMarkIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No calls yet</p>
            {isAdmin && canTrigger && (
              <p className="text-xs mt-1">Click "Trigger Call Now" above to place your first test call</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date / Time</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Number</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Trigger</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {calls.map(c => (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-700">{fmtDateTime(c.started_at)}</td>
                    <td className="px-4 py-2.5 text-gray-700 font-mono text-xs">{c.target_phone_number || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{fmtDuration(c.duration_sec)}</td>
                    <td className="px-4 py-2.5">
                      {c.quality_score != null ? (
                        <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${scoreCls(c.quality_score)}`}>
                          {c.quality_score}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 capitalize">{c.triggered_by}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => openCall(c)}
                        className="px-3 py-1 text-xs rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {activeCall && (
        <CallDetailModal
          call={activeCall}
          onClose={() => setActiveCall(null)}
          onRefresh={handleRefreshActiveCall}
          onReanalyse={handleReanalyseActiveCall}
          onDelete={handleDeleteActiveCall}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
